type PageActionAttributes = Record<string, string | number | boolean>;

/** Query param keys that must not be sent to New Relic (PII, secrets, tokens). */
const REDACTED_QUERY_PARAM_KEYS = new Set([
  'email',
  'code',
  'token',
  'password',
  'secret',
  'key',
  'auth',
  'access_token',
  'refresh_token',
]);

export function getRedactedQueryParams(query: Record<string, unknown>): string | undefined {
  const keys = Object.keys(query);
  if (keys.length === 0) return undefined;
  const safe: Record<string, unknown> = {};
  for (const k of keys) {
    const keyLower = k.toLowerCase();
    const isRedacted = [...REDACTED_QUERY_PARAM_KEYS].some(
      (redacted) => keyLower === redacted || keyLower.includes(redacted)
    );
    safe[k] = isRedacted ? '[REDACTED]' : query[k];
  }
  return JSON.stringify(safe);
}

/** Maps pathname to a template label when pageTemplate is not in pageProps (e.g. join, invite). */
export function getPageTemplateFromPathname(pathname: string): string | undefined {
  if (!pathname) return undefined;
  if (pathname.startsWith('/join')) return 'Join';
  if (pathname.startsWith('/invite')) return 'Invite';
  if (pathname.startsWith('/all-venues')) return 'AllVenues';
  if (pathname.startsWith('/book/confirmation')) return 'BookingConfirmation';
  if (pathname === '/book' || pathname.startsWith('/book/')) return 'Booking';
  return undefined;
}

let currentPageTemplate: string | undefined;

/** Client-only. Called during provider render so child layout effects see the current page. */
export function setCurrentPageTemplate(pageTemplate: string | undefined) {
  currentPageTemplate = pageTemplate;
}

/**
 * Template and URL query for a PageAction. `pageTemplate` comes from the error-context provider's render, not `__NEXT_DATA__` (stale after client navigations) and not `useEffect` (runs after this mark's `useLayoutEffect`).
 * Query comes from `location.search` so it is present before Next hydrates the catch-all slug.
 */
export function getPageActionContext(): PageActionAttributes {
  if (typeof window === 'undefined') return {};

  const attrs: PageActionAttributes = {};
  const pageTemplate = currentPageTemplate ?? getPageTemplateFromPathname(window.location.pathname);
  if (pageTemplate) {
    attrs.pageTemplate = pageTemplate;
  }

  const urlQueryParams = getRedactedQueryParams(
    Object.fromEntries(new URLSearchParams(window.location.search))
  );
  if (urlQueryParams) {
    attrs.urlQueryParams = urlQueryParams;
  }

  return attrs;
}
